import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLogin } from '../features/session.js';
import { useSession } from '../stores/session.js';
import { ApiError } from '../lib/api.js';
import { Input } from '../components/ui/Field.jsx';
import { Button } from '../components/ui/Button.jsx';

/**
 * Staff sign-in.
 *
 * Email and password, no OTP and no self-registration — an account exists here
 * because an administrator created it. The lockout message is deliberately
 * specific because a locked-out operator needs to know it is temporary and how
 * long, not that "something went wrong".
 */

const schema = z.object({
  email: z.string().trim().email('That does not look like an email address.'),
  password: z.string().min(1, 'Enter your password.'),
});

export const Login = () => {
  const navigate = useNavigate();
  const login = useLogin();
  const status = useSession((state) => state.status);

  /**
   * Signing in always lands on the dashboard.
   *
   * The `?next=` the shell adds on an expired session is deliberately ignored.
   * Returning somebody to the deep page they were on sounds considerate and is
   * disorienting in practice: a session usually expires overnight, so they come
   * back to a half-scrolled order from yesterday with no idea what has happened
   * to the shop since. The dashboard answers that first.
   */
  const next = '/';

  const form = useForm({ resolver: zodResolver(schema), defaultValues: { email: '', password: '' } });

  useEffect(() => {
    if (status === 'authenticated') navigate(next, { replace: true });
  }, [status, navigate, next]);

  const submit = form.handleSubmit((values) =>
    login.mutate(values, {
      onSuccess: () => navigate(next, { replace: true }),
      onError: (error) => {
        if (error instanceof ApiError && error.fields) {
          for (const [field, message] of Object.entries(error.fields)) {
            form.setError(field, { message });
          }
          return;
        }

        form.setError('root', {
          message:
            error instanceof ApiError
              ? error.message
              : 'We could not sign you in. Try again in a moment.',
        });
      },
    }),
  );

  return (
    <>
      <Helmet>
        <title>Sign in — Golden Era operations</title>
      </Helmet>

      <main className="grid min-h-screen place-items-center bg-paper px-4">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
              <path d="M12 3.2 22 10v2.4L12 7.1 2 12.4V10z" fill="var(--color-bronze)" />
              <path d="M2 15.1 12 20.8l10-5.7v2.5L12 23.3 2 17.6z" fill="currentColor" />
            </svg>
            <span className="font-display text-xs font-semibold tracking-[0.14em] text-ink uppercase">
              Golden Era
            </span>
          </div>

          <h1 className="mt-8 font-display text-xl tracking-[-0.02em] text-ink">Operations</h1>
          <p className="mt-1.5 text-xs text-muted">
            Staff accounts only. Ask an administrator if you need access.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" noValidate>
            <Input
              label="Email"
              type="email"
              required
              autoFocus
              autoComplete="username"
              {...form.register('email')}
              error={form.formState.errors.email?.message}
            />

            <Input
              label="Password"
              type="password"
              required
              autoComplete="current-password"
              {...form.register('password')}
              error={form.formState.errors.password?.message}
            />

            {form.formState.errors.root && (
              <p role="alert" className="border border-danger/30 bg-danger/8 px-3 py-2 text-2xs text-ink">
                {form.formState.errors.root.message}
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={login.isPending}
              loadingLabel="Signing in"
            >
              Sign in
            </Button>
          </form>

          <p className="mt-8 border-t border-mist pt-5 text-2xs leading-relaxed text-muted">
            Five wrong attempts locks the account for fifteen minutes. Every action taken here is
            recorded in the activity log with your name against it.
          </p>
        </div>
      </main>
    </>
  );
};

export default Login;
